import VisibleView from "components/hoc/VisibleView";
import { memo, useCallback, useEffect, useState } from "react";
import { DeviceInfo, WidgetProps } from "types";
import { fetchDetailDevices } from "service/gatewayService";
import { parseColorToHex } from "utils/functions";
import { ActuatorChart, StackedChart } from "./charts";
import ChartActionDropdown from "../ChartActionDropdown";

interface ActuatorAttribute {
    gateway_id: string;
    device_id: string;
    said: number;
    color: string;
    unit: string;
}

// Map of chart types to their corresponding components for actuators
const CHART_COMPONENT_MAP = {
    SINGLE_LINE: ActuatorChart,
    MULTIPLE_LINES: StackedChart,
    TIME: ActuatorChart,
} as const;

const getComponent = (_source: string, chart: string) => {
    const normalizedChart = chart.toUpperCase();

    return (
        CHART_COMPONENT_MAP[
            normalizedChart as keyof typeof CHART_COMPONENT_MAP
        ] ?? null
    );
};

const ActuatorWidget = ({
    gatewaysConfig,
    zoomLevel,
    panel,
    panel: { attributes, source, chart },
    dashboardId,
    onDelete,
}: WidgetProps) => {
    const [actuatorDetails, setActuatorDetails] = useState<DeviceInfo[]>([]);

    const fetchDeviceInfo = useCallback(async () => {
        const params = (attributes as ActuatorAttribute[]).map(
            ({ gateway_id, device_id, said }) => ({
                gateway_id,
                device_id,
                said: (said ?? 0).toString(),
            })
        );

        try {
            const devices = await fetchDetailDevices(params);
            devices.forEach((d, index) => {
                const attr = attributes[index] as ActuatorAttribute;
                d.chartColor = parseColorToHex(attr.color);
                d.unit = attr.unit === "na" ? d.unit : attr.unit;
            });
            setActuatorDetails(devices);
        } catch (ex) {
            console.error("Failed to fetch actuator details:", ex);
            setActuatorDetails([]);
        }
    }, [attributes]);

    useEffect(() => {
        fetchDeviceInfo();
    }, [fetchDeviceInfo]);

    const Component = getComponent(source, chart);

    if (!Component) return null;

    return (
        <div className="position-relative h-100">
            <ChartActionDropdown
                panelId={panel.uuid}
                dashboardId={dashboardId}
                onDelete={onDelete}
                panel={panel}
                deviceConfig={actuatorDetails}
            />
            <VisibleView>
                <Component
                    gatewaysConfig={gatewaysConfig}
                    zoomLevel={zoomLevel}
                    panel={panel}
                    dashboardId={dashboardId}
                    onDelete={onDelete}
                />
            </VisibleView>
        </div>
    );
};

export default memo(ActuatorWidget);
